import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

export default function BillingSuccess() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState("verifying"); // verifying | done | failed

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    (async () => {
      try {
        await api.post("/billing/confirm", { session_id: sessionId });
        const { data } = await api.get("/billing/status");
        if (data.allowed === true) {
          setState("done");
          if (refresh) await refresh();
          toast.success(t("Subscription active — welcome back!"));
          setTimeout(() => nav("/reports"), 1200);
        } else {
          setState("failed");
        }
      } catch (e) {
        toast.error(apiError(e));
        setState("failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-5" data-testid="billing-success-page">
      <div className="text-center max-w-md">
        {state === "verifying" && (
          <>
            <Loader2 className="animate-spin text-[#F26722] mx-auto" size={40} />
            <h1 className="text-2xl font-extrabold text-[#1A1A1A] mt-5">{t("Confirming your payment…")}</h1>
            <p className="text-zinc-500 mt-2">{t("Please wait while we verify with Stripe.")}</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="text-emerald-500 mx-auto" size={48} />
            <h1 className="text-2xl font-extrabold text-[#1A1A1A] mt-5">{t("You're subscribed!")}</h1>
            <p className="text-zinc-500 mt-2">{t("Taking you to your reports…")}</p>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="text-red-500 mx-auto" size={48} />
            <h1 className="text-2xl font-extrabold text-[#1A1A1A] mt-5">{t("We couldn't confirm payment")}</h1>
            <p className="text-zinc-500 mt-2">{t("If you were charged, please try again in a moment.")}</p>
            <button onClick={() => nav("/subscribe")} data-testid="billing-success-retry-btn"
              className="bg-[#F26722] text-white font-bold px-6 py-3 rounded-xl mt-6 hover:bg-[#D95513] transition-colors">
              {t("Back to subscribe")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
